import * as React from 'react';
import { useCallback, useState } from 'react';

import type { AdminUserRoles_UserRole as AdminUserRole } from '__generated__/graphql-types-do-not-use';

import { PAGE_SIZE } from '../../constants';
import { getNewPaginationOffset } from '../../utils/adminRoleUtils';
import AdminUserRolesProvider from './AdminUserRolesProvider';
import type { GetAdminUserRolesQueryResult } from './AdminUserRolesProvider';

export interface AdminUserRolesDataContext {
  adminUserRoles: AdminUserRole[];
  totalCount: number;
  pageNumber: number;
  adminUserRolesGql?: GetAdminUserRolesQueryResult; // for accessing loading, error, and refetch
  handleSearchQueryChange: (query: string) => void;
  handlePaginationPageChange: (_event: React.ChangeEvent<unknown>, page: number) => void;
}

const INITIAL_CONTEXT_DATA: AdminUserRolesDataContext = {
  adminUserRoles: [],
  totalCount: 1,
  pageNumber: 1,
  handleSearchQueryChange: () => undefined,
  handlePaginationPageChange: () => undefined,
};

/** For test purposes, do not use in actual code */
export const adminUserRolesDataContext = React.createContext<AdminUserRolesDataContext>(INITIAL_CONTEXT_DATA);

export type UseAdminUserRolesData = () => AdminUserRolesDataContext;

export const useAdminUserRolesData: UseAdminUserRolesData = () => React.useContext(adminUserRolesDataContext);

interface Props {
  partnerId: string | number;
  userId: string | number;
}

export const AdminUserRolesData: React.LegacyFunctionComponentWithChildren<Props> = ({
  partnerId,
  userId,
  children,
}) => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [paginationOffset, setPaginationOffset] = useState<string>('');
  const { Provider } = adminUserRolesDataContext;

  const handlePaginationPageChange = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (_event: React.ChangeEvent<unknown>, page: number) => {
      setPageNumber(page);
      const newPaginationOffset = getNewPaginationOffset(page, PAGE_SIZE);
      setPaginationOffset(newPaginationOffset);
    },
    []
  );

  const handleSearchQueryChange = useCallback<(...args: $TSFixMe[]) => $TSFixMe>((query: string) => {
    // if the search query is changed, always fetch from the 1st page of the new result
    setPaginationOffset('');
    setSearchQuery(query);
    setPageNumber(1);
  }, []);

  return (
    <AdminUserRolesProvider
      searchQuery={searchQuery}
      partnerId={`${partnerId}`}
      userId={`${userId}`}
      // the page size is a constant since the pagesize control is not displayed
      pageSize={PAGE_SIZE}
      paginationOffset={paginationOffset}
    >
      {(adminUserRolesResponse) => {
        const adminUserRoles: AdminUserRole[] =
          adminUserRolesResponse.data?.AdminUserRoles?.queryByUser?.edges?.map((edge) => edge.node) ?? [];
        const totalCount = adminUserRolesResponse.data?.AdminUserRoles?.queryByUser?.totalCount ?? 1;
        return (
          <Provider
            value={{
              adminUserRoles,
              totalCount,
              pageNumber,
              adminUserRolesGql: adminUserRolesResponse,
              handleSearchQueryChange,
              handlePaginationPageChange,
            }}
          >
            {children}
          </Provider>
        );
      }}
    </AdminUserRolesProvider>
  );
};
